import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { useTranslation } from 'react-i18next';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { getIncidentStatusColor } from '../../commons/rps-incidence-helpers';

type StatusOption = {
  label: string;
  value: string;
};

type ChangeMaintenanceOrderStatusDialogProps = {
  visible: boolean;
  loading?: boolean;
  /** Estado actual, para no ofrecerlo como cambio. */
  currentStatus?: string | null;
  maintenanceOrderCode?: string;
  onCancel: () => void;
  onConfirm: (statusId: string) => Promise<boolean> | boolean;
};

export const ChangeMaintenanceOrderStatusDialog: React.FC<
  ChangeMaintenanceOrderStatusDialogProps
> = ({
  visible,
  loading = false,
  currentStatus,
  maintenanceOrderCode,
  onCancel,
  onConfirm,
}) => {
    const { t } = useTranslation();

    const [options, setOptions] = React.useState<StatusOption[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<string | null>(
      null
    );
    const [loadingOptions, setLoadingOptions] = React.useState(false);

    React.useEffect(() => {
      if (!visible) return;

      let cancelled = false;

      const loadOptions = async (): Promise<void> => {
        setLoadingOptions(true);
        setSelectedStatus(null);

        try {
          // El backend ya excluye los estados finales: una orden se termina por
          // el flujo de cierre, no cambiando el estado a mano.
          const response =
            await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAssignableStatusOptions();

          if (cancelled) return;

          setOptions(
            (response.data ?? [])
              .filter((option) => option.description)
              .map((option) => ({
                label: option.description as string,
                value: option.id,
              }))
          );
        } catch (error) {
          console.error('Error loading maintenance order statuses:', error);
          if (!cancelled) setOptions([]);
        } finally {
          if (!cancelled) setLoadingOptions(false);
        }
      };

      loadOptions();

      return () => {
        cancelled = true;
      };
    }, [visible]);

    // El estado actual no es un cambio: se muestra aparte y se saca del listado.
    const selectableOptions = React.useMemo(
      () => options.filter((option) => option.label !== currentStatus),
      [options, currentStatus]
    );

    const handleConfirm = async (): Promise<void> => {
      if (!selectedStatus) return;

      const changed = await onConfirm(selectedStatus);
      if (changed) setSelectedStatus(null);
    };

    const footer = (
      <div className="flex justify-content-end gap-2 pt-3">
        <Button
          label={t('cancel')}
          icon="pi pi-times"
          severity="secondary"
          outlined
          disabled={loading}
          onClick={onCancel}
        />

        <Button
          label={t('save')}
          icon="pi pi-check"
          loading={loading}
          disabled={!selectedStatus || loading}
          onClick={handleConfirm}
        />
      </div>
    );

    return (
      <Dialog
        visible={visible}
        modal
        draggable={false}
        resizable={false}
        header={t('pages.maintenance_orders.changeStatus.title')}
        footer={footer}
        style={{ width: '32rem', maxWidth: '95vw' }}
        onHide={() => {
          if (!loading) onCancel();
        }}
      >
        <div className="flex flex-column gap-3">
          {maintenanceOrderCode && (
            <div className="flex flex-column gap-1">
              <span className="text-sm font-medium text-600">
                {t('pages.maintenance_orders.code')}
              </span>
              <span className="font-semibold text-900">
                {maintenanceOrderCode}
              </span>
            </div>
          )}

          {currentStatus && (
            <div className="flex flex-column gap-2">
              <span className="text-sm font-medium text-600">
                {t('pages.maintenance_orders.changeStatus.current')}
              </span>
              <div>
                <Tag
                  value={currentStatus}
                  style={{
                    backgroundColor: getIncidentStatusColor(currentStatus),
                    color: '#fff',
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex flex-column gap-2">
            <label
              htmlFor="maintenance-order-new-status"
              className="text-sm font-medium text-600"
            >
              {t('pages.maintenance_orders.changeStatus.new')}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <Dropdown
              id="maintenance-order-new-status"
              className="w-full"
              value={selectedStatus}
              options={selectableOptions}
              optionLabel="label"
              optionValue="value"
              disabled={loading || loadingOptions}
              placeholder={
                loadingOptions
                  ? t('loading')
                  : t('pages.maintenance_orders.changeStatus.new')
              }
              emptyMessage={t('noResults')}
              onChange={(e) => setSelectedStatus(e.value ?? null)}
            />

            <small className="text-600 line-height-3">
              {t('pages.maintenance_orders.changeStatus.help')}
            </small>
          </div>
        </div>
      </Dialog>
    );
  };

export default ChangeMaintenanceOrderStatusDialog;
